import { CorePermissions, PermissionExplorer } from "app/appPermissions";
import { TFunction } from "i18next";
import { LateralBlockOption, LateralMenu, LateralMenuBlock, LateralMenuItem } from "layouts/common/MenuCommon";
import { useSession } from "providers/SessionProvider";
import React from "react";
import { useTranslation } from "react-i18next";
import { FoundationPaths } from "routes/FoundationRouter";

const buildMenuManager = (pe: PermissionExplorer, t: TFunction): LateralMenuBlock | null => {
  var blocks: LateralBlockOption[] = [];

  ///
  var block1: LateralMenuItem[] = [];
  if (pe.hasOne(
    [
      CorePermissions.UsersManage,
      CorePermissions.UsersUpdate,
      CorePermissions.UsersBlockOrSetAbsence,
      CorePermissions.UsersChangePassword,
      CorePermissions.UsersAssignRole,
      CorePermissions.UsersImpersonate
    ]
  )) {
    block1.push({ path: FoundationPaths.UserList, name: t("App.Menu.Core.Users") });
  }

  if (pe.hasOne([CorePermissions.GroupsManageStatic, CorePermissions.GroupsManageDynamic, CorePermissions.GroupsUpdate]))
    block1.push({ path: FoundationPaths.GroupList, name: t("App.Menu.Core.Groups") });

  if (pe.hasOne([CorePermissions.SecurityRolesManage]))
    block1.push({ path: FoundationPaths.SecurityRoleList, name: t("App.Menu.Core.SecurityRoles") });

  if (pe.hasOne([CorePermissions.UnitsManage, CorePermissions.UnitsUpdate, CorePermissions.UnitTypesManage]))
    block1.push({ path: FoundationPaths.UnitList, name: t("App.Menu.Core.Units") });

  if (block1.length > 0) blocks.push({ items: block1 });

  ///
  var block2: LateralMenuItem[] = [];

  if (pe.hasOne([CorePermissions.TopicsManage]))
    block2.push({ path: FoundationPaths.TopicList, name: t("App.Menu.Core.Topics") });

  if (pe.hasOne([CorePermissions.TagsManage, CorePermissions.TagsBlackListManage]))
    block2.push({ path: FoundationPaths.TagList, name: t("App.Menu.Core.Tags") });

  if (pe.hasOne([CorePermissions.StickersManage]))
    block2.push({ path: FoundationPaths.StickerList, name: t("App.Menu.Core.Stickers") });

  if (pe.hasOne([CorePermissions.BannersManage]))
    block2.push({ path: FoundationPaths.BannerList, name: t("App.Menu.Core.Banners") });

  if (block2.length > 0) blocks.push({ items: block2 });

  ///
  var block3: LateralMenuItem[] = [];
  if (pe.hasOne([CorePermissions.CustomFieldsManage]))
    block3.push({ path: FoundationPaths.CustomField, name: t("App.Menu.Core.CustomFields") });

  if (pe.hasOne([CorePermissions.NotificationEmailTemplatesManage]))
    block3.push({ path: FoundationPaths.NotificationTemplate, name: t("App.Menu.Core.NotificationMessages"), });

  if (block3.length > 0) blocks.push({ items: block3 });

  ///
  var block4: LateralMenuItem[] = [];
  if (pe.hasOne([
    CorePermissions.CalendarManage,
    CorePermissions.AccessHoursManage,
    CorePermissions.SystemUserOptionsConfig,
    CorePermissions.SystemAuthConfigure,
    CorePermissions.SystemCustomizationConfigure,
    CorePermissions.SystemDataLoadConfigure,
    CorePermissions.SystemGamificationConfigure,
    CorePermissions.SystemWelcomeConfigure,
    CorePermissions.SystemTermsConfigure,
  ]))
    block4.push({ path: FoundationPaths.Settings, name: t("App.Menu.Core.Settings"), menuMatch: [FoundationPaths.Settings] });
  if (block4.length > 0) blocks.push({ items: block4 });


  ///
  var block5: LateralMenuItem[] = [];
  if (pe.isAdmin) {
    block5.push({ path: FoundationPaths.ReportList, name: t("App.Menu.Core.Reports"), menuMatch: [FoundationPaths.ReportList] });
  }
  if (block5.length > 0) blocks.push({ items: block5 });

  return {
    header: t("App.Menu.Core.ManageTitle"),
    blocks: blocks
  };
};

const buildMenuOperation = (pe: PermissionExplorer, t: TFunction): LateralMenuBlock | null => {

  var blocks: LateralBlockOption[] = [];

  ///
  var block1: LateralMenuItem[] = [];

  if (pe.hasOne([CorePermissions.SystemSendMessage])) {
    block1.push({ path: FoundationPaths.BroadcastMessage, name: t("App.Menu.Core.BroadcastMessage") });
  }

  if (pe.hasOne([CorePermissions.SystemAuditProcessLog])) {
    block1.push({ path: FoundationPaths.ProcessLog, name: t("App.Menu.Core.ProcessLog") });
  }

  if (pe.hasOne([CorePermissions.SystemAuditDelete])) {
    block1.push({ path: FoundationPaths.ExcludedLog, name: t("App.Menu.Core.LogExcluded") });
  }

  if (block1.length > 0) blocks.push({ items: block1 });

  //
  return blocks.length > 0
    ? {
      header: t("App.Menu.Core.OperationTitle"),
      blocks: blocks
    }
    : null;
};

const buildMenuDataset = (pe: PermissionExplorer, t: TFunction): LateralMenuBlock[] => {
  var modules: (LateralMenuBlock | null)[] = [
    buildMenuManager(pe, t),
    buildMenuOperation(pe, t)
  ];
  return modules.filter(p => p != null) as LateralMenuBlock[];
};

export const FoundationMenu: React.FC = () => {
  const session = useSession();
  const { t } = useTranslation("shared");

  return <LateralMenu dataset={buildMenuDataset(session.permissionExplorer, t)} />;
};
